import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Schedule } from '@data/fms/schedule/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 指定タスクスキップリクエストAPI
 */
export const useTaskSkipAPI = () => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      scheduleId: string,
      taskId: string,
    ): Promise<Result<Schedule, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/schedules/${scheduleId}/tasks/${taskId}/skip`,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postTaskSkip = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, scheduleId: string, taskId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.post_task_skip.failed'),
          });
          setState('hasError');
          return;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          scheduleId,
          taskId,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_task_skip.failed')}: ${getErrorMessage(
              res,
            )}`,
          });
          setState('hasError');
          return;
        }
        notifySuccess({
          message: t('api.fms.post_task_skip.successfully'),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postTaskSkip,
  };
};
